/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import i18next from 'i18next'
import type {BackendModule, ReadCallback} from 'i18next'
import {
  initReactI18next,
  useTranslation as useTranslationUnbound,
  withTranslation as withTranslationUnbound,
} from 'react-i18next'
import type {WithTranslation} from 'react-i18next'
import React from 'react'
import {unstable_batchedUpdates} from 'react-dom'
import hoistNonReactStatics from 'hoist-non-react-statics'
// Most English namespace resources are loaded lazily, one webpack chunk per
// namespace, via the custom backend below -- only a small eager set (see
// EAGER_NAMESPACES in script/split-i18next-namespaces.js) ships inline in
// the main bundle. For non-English locales, the engine capability loads
// namespace translations from the compiled locale file and calls
// i18next.addResourceBundle() per namespace. See
// ui/engine/capabilities/I18n/index.ts.
//
// en.eager.json/en.manifest.json/en/*.json are generated from the committed
// en.json by script/split-i18next-namespaces.js (run after
// script/extract-i18next-strings.js). en.json itself stays complete and
// untouched; it's the source file the translation pipeline will read from.
import enEager from '@instructure/translations/generated/canvas-lms/en.eager.json'
import enLazyManifest from '@instructure/translations/generated/canvas-lms/en.manifest.json'
// pilot locale names; the actual [namespace] -> loader map per locale is dynamic-imported
// below, keyed on lng, so only the active locale's map ever ships to a page
import pilotLocales from 'i18next-locale-bundles-generated'
import {LAZY_ELIGIBLE_NAMESPACES} from '@instructure/translations/scripts/splitNamespaces'
import {isLolcalizeEnabled, lolcalize} from './lolcalize'

const pilotLocaleSet = new Set(pilotLocales as string[])

const lazyEnglishNamespaces = new Set(enLazyManifest as string[])
// A namespace can land in en.eager.json for two different reasons: it was
// never audited as lazy-eligible at all, or it IS lazy-eligible but happens
// to have no value!==key content for English specifically (see
// splitNamespaces()) -- the latter still has real content for other
// locales (CFA-1207). i18next's `ns` init option below is a blanket "keep
// this loaded for whatever language is active" declaration, not an
// English-only one: changeLanguage(lng) sweeps every namespace in `ns` and
// backend-reads any not yet present for lng, so including the second group
// would force-load every one of the pilot locale's real per-namespace
// files as soon as the page's language switches, defeating the split for
// that locale. Excluding LAZY_ELIGIBLE_NAMESPACES from `ns` leaves those to
// load only when a feature bundle actually calls loadNamespaces() for one,
// same as English's own lazy set already does.
const lazyEligibleNamespaces = new Set(LAZY_ELIGIBLE_NAMESPACES as string[])

// Own instance, isolated from the raw 'i18next' singleton -- some
// dependencies (e.g. @instructure/studio-player) call .init() on that same
// singleton, which would otherwise wipe every resource bundle we've added.
const canvasI18next = i18next.createInstance()

// Non-English locales register every namespace up front via addResourceBundle
// (ui/engine/capabilities/I18n), but that data has real translation gaps, so
// i18next falls back to English (fallbackLng) per untranslated key -- which
// means this backend also gets called with lng: 'en' while the page's actual
// language is e.g. 'es'. A busy page can touch dozens of namespaces with at
// least one gap, so treating that like the primary-language case (one
// dynamic import per namespace) turns into dozens of small requests. Instead,
// fetch the combined en.runtime.json once and serve every such fallback
// namespace from it -- one request total, only paid by locales that actually
// need a fallback.
//
// Tracked separately from canvasI18next.language because changeLanguage()
// only publishes the new language after its own resource load finishes, and
// that load is what triggers these fallback reads -- reading it here would
// still say 'en' and send every namespace down the per-namespace branch.
let activeLocale = 'en'
let fallbackBundlePromise: Promise<Record<string, unknown>> | null = null
function loadFallbackBundle(): Promise<Record<string, unknown>> {
  if (!fallbackBundlePromise) {
    fallbackBundlePromise = import(
      /* webpackChunkName: "i18next-en-runtime-fallback" */
      '@instructure/translations/generated/canvas-lms/en.runtime.json'
    ).then(mod => (mod as {default?: Record<string, unknown>}).default ?? mod) as Promise<
      Record<string, unknown>
    >
  }
  return fallbackBundlePromise
}

// English is split per-namespace via a literal-path dynamic import (one context,
// ~360 chunks). Pilot locales are split the same way, but each locale's own
// [namespace] -> loader map is a separate generated module, dynamic-imported here
// once `lng` is a concrete value.
// For any English namespace with no split file, the key IS the English
// string, so returning {} lets i18next fall back to correct English.
const englishNamespaceBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(lng: string, ns: string, callback: ReadCallback) {
    if (lng !== 'en') {
      const backfillCallback: ReadCallback = (err, data) => {
        callback(
          err,
          !err && data && typeof data === 'object'
            ? backfillMissingPluralCategories(data as Record<string, string>)
            : data,
        )
      }
      if (!pilotLocaleSet.has(lng)) {
        // Not piloting the split -- ui/engine/capabilities/I18n already
        // registered this locale via addResourceBundle. undefined (not {})
        // skips addResourceBundle, so no spurious 'added' re-render.
        callback(null, undefined)
        return
      }
      import(
        /* webpackChunkName: "i18next-locale-bundles-[request]" */
        `i18next-locale-bundles-generated/${lng}`
      )
        .then(mod => {
          const namespaceBundles = (mod as {default?: Record<string, () => Promise<unknown>>})
            .default
          const loadNamespace = namespaceBundles?.[ns]
          if (!loadNamespace) {
            // No split file for this (locale, namespace) pair -- undefined, so
            // nothing registers and no 'added' re-render fires.
            callback(null, undefined)
            return
          }
          return loadNamespace().then(nsMod =>
            backfillCallback(null, ((nsMod as {default?: object}).default ?? nsMod) as object),
          )
        })
        .catch(() => backfillCallback(null, {}))
      return
    }
    if (activeLocale !== 'en') {
      loadFallbackBundle()
        .then(all => callback(null, (all[ns] as object) ?? {}))
        .catch(() => callback(null, {}))
      return
    }
    if (!lazyEnglishNamespaces.has(ns)) {
      // Already present via the eager `resources` init option -- passing
      // undefined (not {}) skips addResourceBundle, so this doesn't fire a
      // spurious 'added' event / bindI18nStore re-render on every page load.
      callback(null, undefined)
      return
    }
    import(
      /* webpackChunkName: "i18next-en-[request]" */
      `@instructure/translations/generated/canvas-lms/en/${ns}.json`
    )
      .then(mod => callback(null, (mod as {default?: object}).default ?? mod))
      .catch(() => callback(null, {}))
  },
}

let initialized = false
let lolcalizeInterpolatorHooked = false
let storeEmitBatchedHooked = false

interface StoreEmitter {
  emit: (event: string, ...args: unknown[]) => void
}

// Legacy (ReactDOM.render) roots don't auto-batch, so without this each
// bindI18nStore subscriber commits its own render pass per addResourceBundle().
function installStoreEmitBatchedHook() {
  if (storeEmitBatchedHooked) return
  const store = canvasI18next.store as unknown as StoreEmitter
  const originalEmit = store.emit.bind(store)
  store.emit = (...args: Parameters<StoreEmitter['emit']>) => {
    unstable_batchedUpdates(() => originalEmit(...args))
  }
  storeEmitBatchedHooked = true
}

function installLolcalizeInterpolatorHook() {
  // Lolcalize templates before interpolation when ENV.lolcalize is set.
  if (lolcalizeInterpolatorHooked) return
  const interpolator = canvasI18next.services.interpolator
  if (!interpolator) return
  const originalInterpolate = interpolator.interpolate.bind(interpolator)
  interpolator.interpolate = (str, data, lng, options) => {
    const template = typeof str === 'string' && isLolcalizeEnabled() ? lolcalize(str) : str
    return originalInterpolate(template, data, lng, options)
  }
  lolcalizeInterpolatorHooked = true
}

// Await before rendering: changeLanguage() settles late and handlebars renders once.
async function initI18next(locale?: string): Promise<void> {
  if (locale) activeLocale = locale
  if (initialized) {
    if (locale && locale !== canvasI18next.language) {
      await canvasI18next.changeLanguage(locale)
    }
    return
  }

  // CFA-1207: ignore <html lang> here, always boot as 'en' -- whose eager
  // resources are seeded synchronously below, so i18next's init-time
  // loadResources() sweep over the ns list is a no-op. Booting straight
  // into a non-English lang via html lang would sweep that language
  // instead, before ui/engine/capabilities/I18n's later
  // initI18next(locale)/changeLanguage call has registered anything for
  // it, hitting the backend for every init-time namespace at once.
  const lng = locale || 'en'

  canvasI18next
    .use(englishNamespaceBackend)
    .use(initReactI18next)
    .init({
      lng,
      fallbackLng: 'en',
      // Regional locale files already carry their base language's translations
      // (script/inherit-i18next-translations.js), so there is nothing to
      // resolve through a base language step.
      load: 'currentOnly',
      defaultNS: false,
      keySeparator: false,
      nsSeparator: false,
      ns: Object.keys(enEager).filter(ns => !lazyEligibleNamespaces.has(ns)),
      // Allows resources to seed `en` while other `en` namespaces still load
      // through the backend -- required, or i18next treats `en` as fully
      // loaded from resources alone and never consults the backend.
      partialBundledLanguages: true,
      interpolation: {
        escapeValue: false,
        // Use i18next default {{var}} syntax for newly migrated features.
        // Old @canvas/i18n uses %{var} — migrated strings should be updated.
      },
      // initImmediate: false stays synchronous -- the eager namespaces are
      // in resources already, so init doesn't need to await the backend.
      // The backend is only consulted for namespaces not yet present.
      initImmediate: false,
      react: {
        useSuspense: false,
        // Re-render components when addResourceBundle() loads translations
        // for a new locale, or when a lazy namespace finishes loading via
        // the backend. Without this, components rendered before
        // translations load would show English (or the key) permanently.
        bindI18nStore: 'added',
      },
      resources: {
        en: enEager,
      },
    })

  installLolcalizeInterpolatorHook()
  installStoreEmitBatchedHook()
  initialized = true
}

// Auto-initialize on import. The engine capability calls initI18next(locale)
// once ENV.LOCALE is known, which syncs the language if it differs from the
// default derived from the <html lang> attribute.
initI18next()

const MISSING_PLURAL_FALLBACK_CATEGORIES = ['zero', 'one', 'two', 'few', 'many'] as const

// Backfills missing plural categories from '_other' so an untranslated
// count doesn't silently fall back to English.
function backfillMissingPluralCategories(
  translations: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {...translations}
  for (const key of Object.keys(translations)) {
    if (!key.endsWith('_other')) continue
    const base = key.slice(0, -'_other'.length)
    const otherValue = translations[key]
    for (const category of MISSING_PLURAL_FALLBACK_CATEGORIES) {
      const categoryKey = `${base}_${category}`
      if (!(categoryKey in result)) {
        result[categoryKey] = otherValue
      }
    }
  }
  return result
}

function isPilotLocale(locale: string): boolean {
  return pilotLocaleSet.has(locale)
}

function loadI18nextTranslations(
  locale: string,
  namespaces: Record<string, Record<string, string>>,
) {
  for (const [ns, translations] of Object.entries(namespaces)) {
    canvasI18next.addResourceBundle(
      locale,
      ns,
      backfillMissingPluralCategories(translations),
      true,
      true,
    )
  }
}

// Pins to canvasI18next -- react-i18next's bare hook falls back to whichever
// instance last called .use(initReactI18next), which third-party code can
// steal. Declared generic (not Parameters<>/ReturnType<>) so each call site
// keeps its own namespace-scoped t() type.
function useTranslation<Ns extends Parameters<typeof useTranslationUnbound>[0]>(
  ns?: Ns,
  options?: Parameters<typeof useTranslationUnbound<Ns>>[1],
): ReturnType<typeof useTranslationUnbound<Ns>> {
  return useTranslationUnbound(ns, {...options, i18n: canvasI18next})
}

// Same pinning as useTranslation, for class components. Unlike the hook,
// withTranslation only accepts `i18n` as a prop, so pinning it needs an
// extra forwardRef layer; hoistNonReactStatics keeps static members reachable.
function withTranslation<Ns extends Parameters<typeof withTranslationUnbound>[0]>(
  ns?: Ns,
  options?: Parameters<typeof withTranslationUnbound<Ns>>[1],
) {
  const translate = withTranslationUnbound(ns, options)
  return function pinI18nInstance<C extends Parameters<typeof translate>[0]>(component: C) {
    const Translated = translate(component)
    const Pinned = React.forwardRef((props: object, ref: React.Ref<unknown>) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      React.createElement(Translated as React.ComponentType<any>, {
        ...props,
        i18n: canvasI18next,
        ref,
      }),
    )
    hoistNonReactStatics(Pinned, component)
    return Pinned as unknown as typeof Translated
  }
}

// WrapperOption mirrors the `wrapper`/`wrappers` option from the old @canvas/i18n
// system: a single template string, an ordered array of templates (index 0 = *…*,
// index 1 = **…**, etc.), or an object keyed by the delimiter string itself
// (e.g. {'*': '<i>$1</i>', '**': '<b>$1</b>'}). Object keys must be pure asterisk
// strings ('*', '**', '***', …); non-asterisk delimiters are not supported.
export type WrapperOption = string | string[] | Record<string, string>

// Process the `wrapper`/`wrappers` option from the old @canvas/i18n system.
// In that system, *text* → wrappers[0].replace('$1', text), **text** → wrappers[1].
// i18next does not support this natively; call this helper after t().
//
// Note: this helper does NOT HTML-escape the result. The i18next config sets
// escapeValue: false and migrated call sites render output via React JSX (which
// escapes text nodes) or dangerouslySetInnerHTML with an explicit sanitizer.
// Callers that rely on the old @canvas/i18n auto-escaping must handle escaping
// themselves before passing the result to the DOM.
export function applyWrappers(text: string, wrapper?: WrapperOption): string {
  if (!wrapper) return text

  // Normalise all three input shapes into a map of asterisk-count → template.
  // Using a map (rather than a flat array) means sparse object forms like
  // {'**': tmpl} correctly match \*\*…\*\* instead of \*…\*.
  const wrapperMap: Record<number, string> = {}
  if (typeof wrapper === 'string') {
    wrapperMap[1] = wrapper
  } else if (Array.isArray(wrapper)) {
    wrapper.forEach((tmpl, i) => {
      wrapperMap[i + 1] = tmpl
    })
  } else {
    // Object keyed by delimiter ('*', '**', …). The key length is the asterisk count.
    Object.entries(wrapper).forEach(([delim, tmpl]) => {
      wrapperMap[delim.length] = tmpl
    })
  }

  // Process longer delimiters first (e.g. ** before *) to avoid partial matches.
  let result = text
  const counts = Object.keys(wrapperMap)
    .map(Number)
    .sort((a, b) => b - a)
  for (const count of counts) {
    const regex = new RegExp(`\\*{${count}}([^*]+)\\*{${count}}`, 'g')
    result = result.replace(regex, (_, p1) => wrapperMap[count].replace('$1', p1))
  }
  return result
}

// Replaces the old @canvas/i18n I18n.beforeLabel(text), which wrapped
// this.t('#before_label_wrapper', '%{text}:', {text}) -- a shared,
// locale-specific label-punctuation convention (colon in English, a
// fullwidth colon in zh/zh-Hant), not per-feature text. Uses the shared
// 'common' namespace directly via the i18next singleton so it works
// identically from function components, class components, and
// non-component files without requiring a hook.
//
// Note: the 'common' namespace's "{{text}}:" entry has value===key,
// so script/split-i18next-namespaces.js strips it from en.runtime.json
// (see CFA-1190) and it's never actually loaded into the English
// resource bundle. English correctness relies entirely on i18next's
// missing-key fallback (returning the key itself, interpolated) --
// this is intentional and matches every other value===key entry, but
// means this specific behavior has no explicit English resource entry
// to point to if it ever looks broken.
export function beforeLabel(text: string): string {
  return canvasI18next.t('{{text}}:', {text, ns: 'common'})
}

export {initI18next, isPilotLocale, loadI18nextTranslations, useTranslation, withTranslation}
export {useFireOnceForKey} from './useFireOnceForKey'
export {useNamespaceReadySignal} from './useNamespaceReadySignal'
export type {WithTranslation}
export default canvasI18next
